import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { App } from './app.js';
import { initI18n } from './i18n/index.js';
import { startInSavedLocale } from './i18n/locale.js';

/**
 * The typefaces, **self-hosted** — and this is an I1-adjacent §8 rule rather than a
 * preference.
 *
 * They were `<link>`ed from Google Fonts in `index.html` until 2026-09-08, and the T2-8
 * content-security policy on the SPA document says `style-src 'self'; font-src 'self'`.
 * So production refused both the stylesheet and the font files, and **every screen in an
 * Arabic-first product rendered in `system-ui`** — silently, because the page still works
 * and a real font still appears. Found by loading the deployed site rather than by
 * building it; the build had always succeeded, which is exactly what made the policy look
 * right.
 *
 * Self-hosting rather than widening the CSP is `05-stack-deviations.md` D6's own ruling,
 * made there for map glyphs and identical here: *"fonts/glyph ranges covering Arabic
 * script … bundled/self-hosted, never CDN-fetched at runtime"*, because a CDN fetch fails
 * in precisely the outages §8 calls normal operating conditions. An Arabic-first product
 * whose Arabic face depends on Google being reachable from Beirut and Damascus is not
 * Arabic-first.
 *
 * Imported here rather than `@import`ed in `base.css` so the `@font-face` rules are
 * ordered before the sheet that uses them without depending on CSS `@import` having to be
 * the first statement in the file. Vite bundles the `woff2` into hashed `/assets/`, so the
 * requests are same-origin and the CSP needs no exception at all.
 *
 * Subsets and weights are named one by one instead of pulling the whole family: 400/500/600
 * are the only weights this stylesheet uses, and the Arabic family needs its `latin` subset
 * too — an Arabic screen still shows Latin order references, amounts and email addresses.
 */
import '@fontsource/ibm-plex-sans-arabic/arabic-400.css';
import '@fontsource/ibm-plex-sans-arabic/arabic-500.css';
import '@fontsource/ibm-plex-sans-arabic/arabic-600.css';
import '@fontsource/ibm-plex-sans-arabic/latin-400.css';
import '@fontsource/ibm-plex-sans-arabic/latin-500.css';
import '@fontsource/ibm-plex-sans-arabic/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';

import './styles/base.css';

const i18n = initI18n();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

/**
 * The reader's last language, **before** the first render: a reload or the sign-in redirect used to
 * put every English reader back in Arabic.
 *
 * Awaited since slice 8, because English and French are now a fetched chunk rather than a static
 * import (`i18n/index.ts`). Rendering first and letting the language arrive would paint one frame of
 * Arabic to an English reader — the same defect in a form only a screenshot would catch. An async
 * IIFE rather than a top-level `await`, so the entry chunk needs no TLA support from the browser.
 */
void (async () => {
  await startInSavedLocale(i18n);

  createRoot(container).render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </I18nextProvider>
    </React.StrictMode>,
  );
})();
